import * as React from 'react'

import { type LabelTextProps } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import FormRow from 'modules/core/components/FormRow'
import Country from 'modules/core/components/Inputs/Country'
import Text from 'modules/core/components/Inputs/Text'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useAddListToContractCuesContext } from 'modules/contracts/hooks/useHasContractCue'
import ContractCuesAlert from 'modules/contracts/pages/ContractVersionsPage/ContractCuesAlert'

interface Props {
  mandatory?: boolean
  secondaryLabel?: LabelTextProps['secondaryLabel']
  form: Form
  readonly?: boolean
  showCues?: boolean
}

const AddressInput = ({
  mandatory,
  secondaryLabel,
  form,
  readonly = false,
  showCues = false,
}: Props) => {
  const optionalPlaceholder = i18n.t('common.optional')

  const listOfEmptyFields = Object.keys(form.fields).filter((field) => {
    return !form.get(field).originalValue
  })

  const fieldLabels: Record<string, string> = {
    address: i18n.t('bookkeepers.incidence.details.address'),
  }

  const { addAddressLineOne, addAddressLineTwo, addCity, addZipCode, addState, chooseCountry } =
    trackingEvents.employee.personal

  let translatedList
  const listOfCuedFields = []
  if (showCues) {
    const mandatoryFields = listOfEmptyFields.filter((field) => field !== 'address_line_2')
    if (mandatoryFields.length > 0) listOfCuedFields.push('address')

    translatedList = listOfCuedFields.map((field) => fieldLabels[field])
  }
  useAddListToContractCuesContext('address', translatedList)

  return (
    <div id="address">
      <FormRow>
        <Text
          mandatory={mandatory}
          secondaryLabel={secondaryLabel}
          label={i18n.t('address_input.address_line_1')}
          field={form.get('address_line_1')}
          placeholder={i18n.t('address_input.address_line_1_placeholder')}
          readonly={readonly}
          onBlur={() => track(addAddressLineOne)}
        />
      </FormRow>
      <FormRow>
        <Text
          secondaryLabel={secondaryLabel}
          label={i18n.t('address_input.address_line_2')}
          field={form.get('address_line_2')}
          readonly={readonly}
          placeholder={optionalPlaceholder}
          onBlur={() => track(addAddressLineTwo)}
        />
      </FormRow>
      <FormRow multi>
        <Text
          mandatory={mandatory}
          secondaryLabel={secondaryLabel}
          label={i18n.t('address_input.city')}
          field={form.get('city')}
          placeholder={i18n.t('address_input.city_placeholder')}
          readonly={readonly}
          onBlur={() => track(addCity)}
        />
        <Text
          mandatory={mandatory}
          secondaryLabel={secondaryLabel}
          label={i18n.t('address_input.zip')}
          field={form.get('postal_code')}
          placeholder={i18n.t('address_input.postal_code_placeholder')}
          readonly={readonly}
          onBlur={() => track(addZipCode)}
        />
      </FormRow>
      <FormRow>
        <Text
          mandatory={mandatory}
          secondaryLabel={secondaryLabel}
          label={i18n.t('address_input.state')}
          field={form.get('state')}
          placeholder={i18n.t('address_input.state_placeholder')}
          readonly={readonly}
          onBlur={() => track(addState)}
        />
      </FormRow>
      <FormRow>
        <Country
          mandatory={mandatory}
          secondaryLabel={secondaryLabel}
          label={i18n.t('address_input.country')}
          field={form.get('country')}
          value="Spain"
          readonly={readonly}
          onBlur={() => track(chooseCountry)}
        />
      </FormRow>
      {!readonly && showCues && <ContractCuesAlert fields={translatedList}></ContractCuesAlert>}
    </div>
  )
}

export default AddressInput
